// Pagina pubblica raggiunta cliccando il link nella mail di reset.
// URL atteso: /reset-password?token=XXX&email=user@example.com
//
// Flusso:
//   1. Legge token + email dai query params
//   2. Mostra form nuova password
//   3. POST /api/auth/reset-password/confirm
//   4. Successo → redirect /login con messaggio

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, Alert, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash, Lock } from 'react-bootstrap-icons';
import { authService } from '../../../services';
import {
  isPasswordValid,
  validatePassword,
} from '../../../utils/passwordvalidation';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Token o email mancanti — link non valido
  if (!token || !email) {
    return (
      <div className="rp-page">
        <div className="rp-card">
          <div className="rp-icon">🔗</div>
          <h1 className="rp-title">Link non valido</h1>
          <p className="rp-sub">
            Il link per reimpostare la password non è valido o è scaduto.
            Richiedi un nuovo link dalla pagina di login.
          </p>
          <Link to="/" className="rp-btn-primary">
            Torna alla home
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="rp-page">
        <div className="rp-card">
          <div className="rp-icon">✅</div>
          <h1 className="rp-title">Password aggiornata</h1>
          <p className="rp-sub">
            La tua password è stata reimpostata con successo. Puoi ora accedere
            con le nuove credenziali.
          </p>
          <Link to="/" className="rp-btn-primary">
            Vai alla home
          </Link>
        </div>
      </div>
    );
  }

  const validation = validatePassword(password);
  const passwordsMatch = password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Inserisci la nuova password.');
      return;
    }
    if (!isPasswordValid(validation)) {
      setError('La password non rispetta i requisiti di sicurezza.');
      return;
    }
    if (!confirm) {
      setError('Conferma la nuova password.');
      return;
    }
    if (!passwordsMatch) {
      setError('Le password non coincidono.');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPasswordConfirm(email, token, password);
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Token non valido o scaduto. Richiedi un nuovo link.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-page">
      <div className="rp-card">
        <div className="rp-icon">🔑</div>
        <h1 className="rp-title">Nuova password</h1>
        <p className="rp-sub">
          Scegli una nuova password per <strong>{email}</strong>.
        </p>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError('')}
            className="small mb-3"
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <Form.Label className="rp-label" htmlFor="rp-password">
            Nuova password
          </Form.Label>
          <div className="rp-input-wrap">
            <span className="rp-input-icon">
              <Lock size={16} />
            </span>
            <Form.Control
              id="rp-password"
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="rp-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label="Mostra/nascondi password"
            >
              {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* Requisiti password */}
          {password.length > 0 && !isPasswordValid(validation) && (
            <div className="rp-hints">
              {[
                { ok: validation.hasMinLength, text: 'Almeno 8 caratteri' },
                { ok: validation.hasUpperCase, text: 'Una lettera maiuscola' },
                { ok: validation.hasLowerCase, text: 'Una lettera minuscola' },
                { ok: validation.hasNumber, text: 'Un numero' },
                { ok: validation.hasSymbol, text: 'Un simbolo (!@#$%...)' },
              ].map(({ ok, text }) => (
                <div
                  key={text}
                  className={`rp-hint-item ${ok ? 'rp-hint--ok' : ''}`}
                >
                  <i
                    className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-x-circle'} me-1`}
                  />
                  {text}
                </div>
              ))}
            </div>
          )}

          <Form.Label className="rp-label" htmlFor="rp-confirm">
            Conferma password
          </Form.Label>
          <div className="rp-input-wrap">
            <span className="rp-input-icon">
              <Lock size={16} />
            </span>
            <Form.Control
              id="rp-confirm"
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className={
                confirm.length > 0 && !passwordsMatch ? 'is-invalid' : ''
              }
            />
            <button
              type="button"
              className="rp-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label="Mostra/nascondi conferma"
            >
              {showConfirm ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {confirm.length > 0 && !passwordsMatch && (
            <p className="rp-match-error">Le password non coincidono.</p>
          )}

          <button
            type="submit"
            className="rp-btn-primary mt-3"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Aggiornamento...
              </>
            ) : (
              'Aggiorna password'
            )}
          </button>
        </Form>

        <p className="text-center small text-muted mt-3 mb-0">
          <Link to="/" className="rp-back-link">
            ← Torna alla home
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
